import { useField } from "formik"
import { useState } from "react"
import FormField from "~/ui-rtk/components/ui/controls/FormField"
import { DatePicker, Input } from "~/ui-rtk/components/ui/controls"
import { toCurrencyWithCommas } from "~/ui-rtk/utils/number-utils"
import "./index.scss"

interface KeyEventFormFieldProps {
  id: string
  type: string
  label: string
  placeholder?: string
  errors: Record<string, string>
  touched: Record<string, boolean>
}

interface CurrencyInputProps {
  id: string
  label: string
  placeholder?: string
  isError?: boolean
}

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  id,
  label,
  placeholder,
  isError,
}) => {
  const [field, meta, helpers] = useField(id)
  const [displayValue, setDisplayValue] = useState("")

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const cursorPosition = e.target.selectionStart

    // Remove all non-numeric characters
    const numericValue = inputValue.replace(/[^0-9]/g, "")

    // If the value is empty, reset everything
    if (!numericValue) {
      void helpers.setValue("")
      setDisplayValue("")
      return
    }

    // Convert to cents (move decimal point 2 places to the left)
    const dollars = Number(numericValue) / 100

    // Update the form value
    void helpers.setValue(dollars)

    // Format the display value
    const formattedValue = toCurrencyWithCommas(dollars, "", true)
    setDisplayValue(formattedValue)

    // Restore cursor position if it exists
    if (cursorPosition !== null) {
      setTimeout(() => {
        const input = document.getElementById(id) as HTMLInputElement
        if (input) {
          // Calculate new cursor position
          const newPosition =
            cursorPosition + (formattedValue.length - inputValue.length)
          input.setSelectionRange(newPosition, newPosition)
        }
      }, 0)
    }
  }

  const handleBlur = () => {
    if (field.value) {
      const formattedValue = toCurrencyWithCommas(field.value, "", true)
      setDisplayValue(formattedValue)
    }
  }

  return (
    <div className="relative flex flex-col items-start w-full">
      {label && (
        <label className="text-white text-sm font-medium px-0.5 mb-1">
          {label}
        </label>
      )}
      <Input
        id={id}
        name={id}
        type="text"
        placeholder={placeholder}
        value={displayValue}
        fullWidth
        onChange={handleChange}
        onBlur={handleBlur}
        isError={isError}
        className="bg-basic-blue w-full text-white border-basic-blue h-9 px-3 rounded-md focus:ring-2 focus:ring-basic-blue focus:ring-offset-2 focus:ring-offset-basic-dark-blue focus:outline-none"
      />
      {meta.error && meta.touched && (
        <div className="input-error-message top-full">{meta.error}</div>
      )}
    </div>
  )
}

const KeyEventFormField: React.FC<KeyEventFormFieldProps> = ({
  id,
  type,
  label,
  placeholder,
  errors,
  touched,
}) => {
  const [field, meta, helpers] = useField(id)

  if (id === "spend") {
    return (
      <CurrencyInput
        id={id}
        label={label}
        placeholder={placeholder}
        isError={!!(errors[id] && touched[id])}
      />
    )
  }

  if (type === "date") {
    return (
      <div className="relative flex flex-col items-start w-full date-picker-container">
        {label && (
          <label className="text-white text-sm font-medium px-0.5 mb-1">
            {label}
          </label>
        )}
        <DatePicker
          selected={field.value ? new Date(field.value) : null}
          onChange={(date: Date) => helpers.setValue(date)}
          onBlur={() => helpers.setTouched(true)}
          className="bg-basic-blue text-sm text-white border-basic-blue h-9 px-3 rounded-md focus:ring-2 focus:ring-basic-blue focus:ring-offset-2 focus:ring-offset-basic-dark-blue focus:outline-none w-full"
          calendarClassName="ui-rtk-date-picker"
          dateFormat="MMM d, yyyy"
          placeholderText={placeholder}
        />
        {meta.error && meta.touched && (
          <div className="input-error-message top-full">{meta.error}</div>
        )}
      </div>
    )
  }

  return (
    <FormField
      id={id}
      type={type}
      label={label}
      placeholder={placeholder}
      isError={!!(errors[id] && touched[id])}
      className="p-0"
      classes={{
        label: "text-white text-sm font-medium",
        input:
          "bg-basic-blue text-white border-basic-blue h-9 px-3 rounded-md focus:ring-2 focus:ring-basic-blue focus:ring-offset-2 focus:ring-offset-basic-dark-blue focus:outline-none",
      }}
    />
  )
}

export default KeyEventFormField
